import React, { useState, useEffect } from "react";
import { DisplayEvent } from "../types/events";
import styles from "../styles/EventBanner.module.css";

interface EventBannerProps {
  event: DisplayEvent | null;
  onClose: () => void;
  duration?: number; // Display duration in ms (default: 4000)
}

const EventBanner: React.FC<EventBannerProps> = ({ event, onClose, duration = 4000 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (event) {
      setIsVisible(true);
      setIsClosing(false);

      // Auto-close after duration
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, duration]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, 300);
  };

  const handleBannerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (event?.explorerUrl) {
      window.open(event.explorerUrl, "_blank", "noopener,noreferrer");
    }
  };

  if (!event || !isVisible) return null;

  const getEventIcon = (): string => {
    switch (event.type) {
      case "game_created":
        return "🎮";
      case "payout_sent":
        return "💰";
      case "dxf_reward_minted":
        return "🪙";
      default:
        return "📝";
    }
  };

  const getEventTypeClass = (): string => {
    switch (event.type) {
      case "game_created":
        return styles.gameCreated;
      case "payout_sent":
        return styles.payoutSent;
      case "dxf_reward_minted":
        return styles.dxfRewardMinted;
      default:
        return styles.default;
    }
  };

  return (
    <div
      className={`${styles.eventBanner} ${getEventTypeClass()} ${isClosing ? styles.closing : ""} ${event.isNew ? styles.newEvent : ""}`}
      onClick={handleBannerClick}
      title="Click to view transaction in explorer"
    >
      <div className={styles.eventContent}>
        <div className={styles.eventIcon}>{getEventIcon()}</div>
        <div className={styles.eventMessage}>{event.message}</div>
        <div className={styles.eventLinkIcon}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="currentColor"
            className={styles.arrowIcon}
            style={{ transform: "translateY(2px)" }}
          >
            <path d="M3.5 3.5V12.5H12.5V8H14V12.5C14 13.0523 13.5523 13.5 13 13.5H3C2.44772 13.5 2 13.0523 2 12.5V3C2 2.44772 2.44772 2 3 2H7.5V3.5H3.5Z" />
            <path d="M14 2V7H12.5V4.20711L6.85355 9.85355L5.79289 8.79289L11.4393 3.14645H8.5V2H14Z" />
          </svg>
        </div>
      </div>

      {/* <button 
        className={styles.eventCloseBtn} 
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        aria-label="Close banner"
      >
        ×
      </button> */}

      {event.isNew && <div className={styles.newEventPulse}></div>}
    </div>
  );
};

export default EventBanner;
